import { Image } from 'pobble-components/dist/image'
import React, { useLayoutEffect, useMemo } from 'react'
import { useEffect } from 'react'
import { useRef } from 'react'
import injectSheet from 'react-jss/lib/injectSheet'
import { useCalendarData } from './calendar.helpers'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import { apiClient } from 'pobble-components/dist/api'
import { useState } from 'react'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { UpsellModal } from '../upsell'
import PobbleLink from 'pobble-components/dist/pobble-link'
import duckRocket from '../duck-rocket.gif'
import { PromptsLayout } from '../../../prompts-layout'
import classNames from 'classnames'

export const CalendarUpsell = injectSheet({
  img: {
    width: '175px !important',
    height: '175px !important',
    margin: '0 auto 24px !important'
  },
  note: {
    maxWidth: '224px',
    font: 'var(--body-)'
  },
  cta: {
    margin: '32px 0 0',
    background: 'var(--dark-blue) !important',
    borderColor: 'var(--dark-blue) !important'
  }
})(({ classes, onToday, ...props }) => {
  return (
    <UpsellModal id="calendar" {...props}>
      <h2>Unlock unlimited writing inspiration!</h2>
      <div className={classes.imgHolder}>
        <img
          className={classes.img}
          src={duckRocket}
          width="175"
          height="175"
          alt="Duck in a rocket"
        />
      </div>
      <p>
        Your free plan only allows access to{` `}
        <PobbleLink
          onClick={() => {
            window.scrollTo({ top: 0 })
          }}
          as={Link}
          to="/prompt">
          today’s Pobble 365 lesson
        </PobbleLink>. With a <strong>Teaching and Learning</strong>{' '}
        subscription, you unlock unlimited access to all of Pobble’s inspiring
        content, including <strong>all Pobble 365 lessons</strong>
      </p>
    </UpsellModal>
  )
})

let pobble365cache = null

export const Calendar = injectSheet({
  container: {
    background: '#191919',
    color: '#fff',
    padding: '56px 24px 80px'
  },
  header: {
    position: 'sticky',
    zIndex: '110',
    top: '50px',
    '@media (min-width: 769px)': {
      top: '70px'
    }
  },
  month: {
    maxWidth: '1200px',
    margin: '0 auto 48px',
    '@media (min-width: 1024px)': {
      marginBottom: '64px'
    }
  },
  daysGrid: {
    position: 'relative',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px'
  },
  dayGridItem: {
    font: 'var(--body)',
    paddingBottom: '56.2%',
    position: 'relative'
  },
  day: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    font: 'var(--body)',
    '& > *': {
      pointerEvents: 'none'
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    '&:hover': {
      '& $dayContent': {
        background: 'rgba(0, 0, 0, .3)',
        '@media (min-width: 768px)': {
          background: '#000'
        }
      }
    }
  },
  monthDay: {
    background: '#242424',
    '&:focus': {
      outline: '4px solid rgba(255, 255, 255, 1)'
    }
  },
  dayContent: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, .6)',
    '@media (min-width: 768px)': {
      width: '30px',
      height: '30px',
      background: '#000'
    },
    '@media (min-width: 1024px)': {
      font: 'var(--heading--)'
    }
  },
  today: {
    outline: '4px solid var(--yellow)',
    '& $dayContent': {
      background: 'var(--yellow) !important',
      color: '#000'
    },
    '&:after': {
      content: '"Free today"',
      position: 'absolute',
      right: '6px',
      top: '6px',
      textTransform: 'uppercase',
      font: 'var(--body---)',
      padding: '4px 8px',
      background: 'var(--yellow) !important',
      borderRight: '1px solid var(--gray)',
      borderRadius: '16px',
      color: '#000',
      fontWeight: 'bold',
      display: 'none',
      '@media (min-width: 768px)': {
        display: 'block'
      }
    }
  },
  subscribed: {
    '& $today:after': {
      content: '"Today"'
    }
  },
  activeDay: {
    outline: '4px solid #fff',
    zIndex: '10',
    '& $dayContent': {
      background: '#fff !important',
      color: 'var(--gray)',
      fontWeight: 'bold'
    }
  },
  monthTitle: {
    font: 'var(--heading)',
    margin: '0 0 24px',
    textAlign: 'center',
    '@media (min-width: 1024px)': {
      font: 'var(--heading_)',
      margin: '0 0 32px'
    }
  },
  crown: {
    display: 'none',
    '@media (min-width: 1024px)': {
      display: 'block',
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, .5)',
      fontSize: '10px',
      color: 'var(--yellow)',
      zIndex: '100',
      '& svg': {
        position: 'absolute',
        right: '4px',
        top: '4px'
      }
    }
  }
})(({ yearAndMonth, classes, onClose, activeDay }) => {
  const calendarData = useCalendarData()
  const activeMonthContainer = useRef(null)
  const [presentations, setPresentations] = useState(null)
  const [upsellModalOpen, setUpsellModalOpen] = useState(false)
  const match = useRouteMatch()

  const [todayId, setTodayId] = useState(null)

  const date = new Date()
  const activeMonth = date.getMonth()
  const { hasAccessToTL } = useSubscriptionDetails()

  const { hadTrialBefore, individualPlan } = useSubscriptionDetails()
  const showFreeTrial = !hadTrialBefore && !individualPlan

  useEffect(() => {
    apiClient
      .get('/v3/p365/today', {
        params: {
          count: 1,
          from: 0
        }
      })
      .then(res => {
        setTodayId(res.data.presentations[0].id)
      })
  }, [])

  useLayoutEffect(
    () => {
      if (activeMonthContainer.current) {
        const target = activeMonthContainer.current
        const y = target.getBoundingClientRect().top + window.pageYOffset - 200
        window.scrollTo({ top: y })
      }
    },
    [activeMonthContainer]
  )

  useEffect(() => {
    if (pobble365cache) {
      return setPresentations(pobble365cache)
    }
    apiClient.get('/v3/p365/calendar').then(res => {
      pobble365cache = res.data.calendar
      setPresentations(res.data.calendar)
    })
  }, [])

  const history = useHistory()

  const memoizedCalendarJSX = useMemo(
    () => {
      return (
        <>
          {calendarData.map((month, monthIndex) => {
            const isActiveMonth = activeMonth === monthIndex

            return (
              <div
                key={monthIndex}
                className={classes.month}
                ref={isActiveMonth ? activeMonthContainer : undefined}>
                <h2 className={classes.monthTitle}>{month.title}</h2>
                <div className={classes.daysGrid}>
                  {month.days.map(day => {
                    const presentationDay =
                      presentations &&
                      day.isCurrentMonth &&
                      presentations[monthIndex][day.dayOfMonth - 1]

                    const presentation =
                      presentationDay && presentationDay.presentation

                    const isRestricted = false

                    return (
                      <div key={day.dateString} className={classes.dayGridItem}>
                        {!day.isCurrentMonth && (
                          <span className={classes.day} />
                        )}
                        {day.isCurrentMonth && (
                          <ButtonElement
                            onClick={
                              !presentation
                                ? undefined
                                : () => {
                                    if (isRestricted) {
                                      return setUpsellModalOpen(presentation.id)
                                    }
                                    window.scrollTo({ top: 0 })
                                    history.push(`/prompt/${presentation.id}`)
                                  }
                            }
                            className={`${classes.day} ${
                              isRestricted
                                ? `metrics__restricted--calendar ${
                                    showFreeTrial
                                      ? 'metrics__restricted--calendar--trial'
                                      : ''
                                  }`
                                : ''
                            } ${day.isCurrentMonth ? classes.monthDay : ''} ${
                              presentation &&
                              presentation.id === match.params.id
                                ? classes.activeDay
                                : ''
                            } ${
                              presentation && todayId === presentation.id
                                ? classes.today
                                : ''
                            }`}>
                            {day.isCurrentMonth && (
                              <>
                                {presentation && (
                                  <Image
                                    loading={isActiveMonth ? 'eager' : 'lazy'}
                                    src={
                                      presentation.image &&
                                      presentation.image.thumb_url
                                    }
                                  />
                                )}
                                <span className={`${classes.dayContent}`}>
                                  {day.dayOfMonth}
                                </span>
                                {isRestricted && (
                                  <span className={classes.crown}>
                                    <FontAwesomeIcon icon={faCrown} />
                                  </span>
                                )}
                              </>
                            )}
                          </ButtonElement>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </>
      )
    },
    [
      activeMonth,
      classes,
      calendarData,
      match.params.id,
      history,
      presentations,
      todayId,
      showFreeTrial
    ]
  )

  return (
    <PromptsLayout>
      <div
        className={classNames(classes.container, {
          [classes.subscribed]: hasAccessToTL
        })}>
        {memoizedCalendarJSX}
        <CalendarUpsell
          lessonId={upsellModalOpen}
          isOpen={!!upsellModalOpen}
          requestClose={() => setUpsellModalOpen(false)}
          onToday={onClose}
        />
      </div>
    </PromptsLayout>
  )
})
